import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faCheck, faPlus, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import HeroSection from '../dashboard/sections/HeroSection';
import Footer from '../ui/Footer';
import HamsterFloatingButton from '../HamsterFloatingButton';
import { useUserInfo } from '../../contexts/UserContext';
import { dashboardService } from '../../services/dashboardService';
import { myDeskService } from '../../services/myDeskService';
import { useFlashMessage } from '../FlashMessage';
import MyDeskGrid from './MyDeskGrid';
import AddWidgetDrawer from './AddWidgetDrawer';
import { pushBounded } from './layoutMath';
import './MyDeskPage.css';

const SAVE_DEBOUNCE_MS = 600;

const newWidgetId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `w-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const sameIds = (a, b) => a.length === b.length && a.every((w) => b.some((x) => x.id === w.id));

/**
 * My Desk: the per-user widget board.
 *
 * The backend owns the access rules. The catalog it returns is already
 * filtered to what this user may add, and every widget's data call is
 * re-checked server side, so this page only arranges what it is given.
 */
const MyDeskPage = () => {
  const { userInfo, updateUserInfo } = useUserInfo();
  const { showError } = useFlashMessage();

  const [widgets, setWidgets] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Changes made while editing, newest last: [{ label, widgets }] where
  // `widgets` is the layout before that change.
  const [history, setHistory] = useState([]);
  const [announcement, setAnnouncement] = useState('');

  // Last layout confirmed by the server, used to roll back a failed save.
  const savedWidgetsRef = useRef([]);
  const saveTimerRef = useRef(null);
  // The layout on screen, readable from handlers without a stale closure.
  const widgetsRef = useRef([]);
  // Set by the grid: { capture, isBusy } for animating changes made here.
  const editorRef = useRef(null);

  const commitWidgets = useCallback((next) => {
    widgetsRef.current = next;
    setWidgets(next);
  }, []);

  // Screen reader updates. Repeating the same text still gets announced
  // because it alternates with a trailing no-break space.
  const announce = useCallback((message) => {
    setAnnouncement((prev) => (prev === message ? `${message} ` : message));
  }, []);

  // Keep the latest context setter without making the load effect depend on
  // its identity: the board must load exactly once per mount.
  const updateUserInfoRef = useRef(updateUserInfo);
  useEffect(() => { updateUserInfoRef.current = updateUserInfo; }, [updateUserInfo]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [layout, catalogData, userInfoData] = await Promise.all([
          myDeskService.getLayout(),
          myDeskService.getCatalog(),
          dashboardService.getUserInfo(),
        ]);
        if (cancelled) return;
        const list = Array.isArray(layout?.widgets) ? layout.widgets : [];
        commitWidgets(list);
        savedWidgetsRef.current = list;
        setCatalog(Array.isArray(catalogData?.widgets) ? catalogData.widgets : []);
        updateUserInfoRef.current(userInfoData);
        setLoadError(null);
      } catch (err) {
        console.error('My Desk: failed to load board', err);
        if (!cancelled) setLoadError('My Workspace could not be loaded. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [commitWidgets]);

  // Drop a pending debounced save on unmount.
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const catalogByType = useMemo(() => {
    const map = {};
    catalog.forEach((entry) => { map[entry.type] = entry; });
    return map;
  }, [catalog]);

  // Persist optimistically. On failure restore the last server-confirmed
  // layout so the screen never shows an arrangement that was not saved.
  // Reorder and resize are debounced (they come in bursts); adding a widget
  // saves at once, because the new card's data call is only answered once
  // the widget exists in the stored layout.
  const save = useCallback(async (nextWidgets) => {
    setSaving(true);
    try {
      const result = await myDeskService.saveLayout(nextWidgets);
      const confirmed = Array.isArray(result?.widgets) ? result.widgets : nextWidgets;
      savedWidgetsRef.current = confirmed;
      // The server may drop widgets the user may no longer see.
      if (confirmed.length !== nextWidgets.length) commitWidgets(confirmed);
    } catch (err) {
      console.error('My Desk: failed to save layout', err);
      commitWidgets(savedWidgetsRef.current);
      // The undo steps describe layouts that were never saved.
      setHistory([]);
      showError('Your My Workspace layout could not be saved. The previous layout has been restored.');
    } finally {
      setSaving(false);
    }
  }, [showError, commitWidgets]);

  const persist = useCallback((nextWidgets, { immediate = false } = {}) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (immediate) {
      save(nextWidgets);
      return;
    }
    saveTimerRef.current = setTimeout(() => save(nextWidgets), SAVE_DEBOUNCE_MS);
  }, [save]);

  // One layout change: show it, save it, and while editing remember the
  // layout before it so it can be undone.
  const applyChange = useCallback((updater, { immediate = false, label } = {}) => {
    const previous = widgetsRef.current;
    const next = typeof updater === 'function' ? updater(previous) : updater;
    commitWidgets(next);
    persist(next, { immediate });
    if (editMode && label) setHistory((h) => pushBounded(h, { label, widgets: previous }));
  }, [commitWidgets, persist, editMode]);

  const handleReorder = useCallback((next, { label } = {}) => applyChange(next, { label }), [applyChange]);

  const handleResize = useCallback((widgetId, size, { label } = {}) => {
    applyChange((current) => current.map((w) => (w.id === widgetId ? { ...w, size } : w)), { label });
  }, [applyChange]);

  const handleRemove = useCallback((widgetId, { label } = {}) => {
    applyChange((current) => current.filter((w) => w.id !== widgetId), { immediate: true, label });
  }, [applyChange]);

  const handleAdd = useCallback((type) => {
    const entry = catalogByType[type];
    if (!entry) return;
    applyChange((current) => ([
      ...current,
      { id: newWidgetId(), type, size: entry.default_size || 'third', config: {} },
    ]), { immediate: true, label: `Added ${entry.title || type}` });
  }, [applyChange, catalogByType]);

  const handleUndo = useCallback(() => {
    if (!history.length) return;
    if (editorRef.current && editorRef.current.isBusy()) return;
    const { label, widgets: previous } = history[history.length - 1];
    setHistory(history.slice(0, -1));
    if (editorRef.current) editorRef.current.capture();
    const current = widgetsRef.current;
    commitWidgets(previous);
    // Bringing a widget back (or taking one away) must reach the server
    // before the card asks for its data.
    persist(previous, { immediate: !sameIds(previous, current) });
    announce(`Undid: ${label}.`);
  }, [history, commitWidgets, persist, announce]);

  const toggleEditMode = useCallback(() => {
    if (editMode) setHistory([]);
    setEditMode(!editMode);
  }, [editMode]);

  // Cmd or Ctrl+Z undoes while editing, except inside a text field.
  useEffect(() => {
    if (!editMode) return undefined;
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      if (String(event.key).toLowerCase() !== 'z') return;
      const target = event.target;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      event.preventDefault();
      handleUndo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editMode, handleUndo]);

  // Pinning is a server action: the server checks the object is visible and
  // returns the whole layout, which becomes the new confirmed state.
  const handlePin = useCallback(async (entityType, objectId) => {
    try {
      const result = await myDeskService.pin(entityType, objectId);
      const confirmed = Array.isArray(result?.widgets) ? result.widgets : null;
      if (confirmed) {
        savedWidgetsRef.current = confirmed;
        commitWidgets(confirmed);
      }
    } catch (err) {
      console.error('My Desk: pin failed', err);
      showError(err?.response?.data?.error || 'This item could not be pinned.');
    }
  }, [showError, commitWidgets]);

  const handlePinSearch = useCallback(async (query) => {
    const result = await myDeskService.pinSearch(query);
    return Array.isArray(result?.results) ? result.results : [];
  }, []);

  const addedTypes = useMemo(() => new Set(widgets.map((w) => w.type)), [widgets]);
  const pinnedKeys = useMemo(() => new Set(
    widgets
      .filter((w) => typeof w.type === 'string' && w.type.startsWith('pinned_') && w.config?.object_id !== undefined)
      .map((w) => `${w.type.replace('pinned_', '')}:${w.config.object_id}`)
  ), [widgets]);

  const lastChange = history.length ? history[history.length - 1].label : null;

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="mydesk-page">
        <HeroSection userInfo={userInfo} />

        <div className="mydesk-toolbar">
          <div className="mydesk-toolbar-text">
            <h2 className="mydesk-title">My Workspace</h2>
            <p className="mydesk-subtitle">Your pinned work and everything waiting on you.</p>
          </div>
          <div className="mydesk-toolbar-actions">
            {saving && <span className="mydesk-saving" role="status">Saving</span>}
            {editMode && (
              <button
                type="button"
                className="mydesk-btn mydesk-btn-ghost"
                onClick={handleUndo}
                disabled={!history.length}
                title={lastChange ? `Undo: ${lastChange}` : 'Nothing to undo yet'}
              >
                <FontAwesomeIcon icon={faRotateLeft} />
                {history.length ? `Undo (${history.length})` : 'Undo'}
              </button>
            )}
            <button
              type="button"
              className={`mydesk-btn mydesk-btn-ghost ${editMode ? 'is-active' : ''}`}
              onClick={toggleEditMode}
              disabled={loading || !!loadError}
            >
              <FontAwesomeIcon icon={editMode ? faCheck : faPen} />
              {editMode ? 'Done' : 'Edit layout'}
            </button>
            <button
              type="button"
              className="mydesk-btn mydesk-btn-primary"
              onClick={() => setDrawerOpen(true)}
              disabled={loading || !!loadError}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add widget
            </button>
          </div>
        </div>

        {loadError && (
          <div className="mydesk-load-error" role="alert">{loadError}</div>
        )}

        {!loadError && (
          <MyDeskGrid
            widgets={widgets}
            catalogByType={catalogByType}
            loading={loading}
            editMode={editMode}
            onReorder={handleReorder}
            onResize={handleResize}
            onRemove={handleRemove}
            onOpenDrawer={() => setDrawerOpen(true)}
            editorRef={editorRef}
            announce={announce}
          />
        )}

        <div className="mydesk-sr-only" role="status" aria-live="polite" aria-atomic="true">{announcement}</div>

        <AddWidgetDrawer
          open={drawerOpen}
          catalog={catalog}
          addedTypes={addedTypes}
          pinnedKeys={pinnedKeys}
          onAdd={handleAdd}
          onPin={handlePin}
          onPinSearch={handlePinSearch}
          onClose={() => setDrawerOpen(false)}
        />

        <HamsterFloatingButton />
        <Footer />
      </div>
    </Layout>
  );
};

export default MyDeskPage;
